import * as React from "react";
import { styled } from "@mui/material/styles";
import { AppBar, Box, Button, Toolbar, IconButton, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { showSearchPopUP, toggleSideBar } from "../../store/slices/ui-slice";

import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import { logo } from "../../assets/images";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import NotificationMenu from "./component/NotificationMenu";
import UserAvatarMenu from "./component/UserAvatarMenu";
import COMMAND_CENTER_COLORS from "../../constants/commandCenterColors";

import { AppConstants } from "../../constants";
import { mono } from "../../Theme/fonts";

// Styled search container - Intelligence Dashboard Format
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "3px",
  backgroundColor: COMMAND_CENTER_COLORS.background.primary,
  border: `1px solid ${COMMAND_CENTER_COLORS.borders.light}`,
  "&:hover": {
    borderColor: COMMAND_CENTER_COLORS.accent.primary,
    backgroundColor: COMMAND_CENTER_COLORS.background.accent,
  },
  "&:hover .search-icon-el": {
    color: COMMAND_CENTER_COLORS.accent.primary,
  },
  marginRight: theme.spacing(2),
  marginLeft: "1rem",
  width: "100%",
  cursor: "pointer",
  transition: "all 0.2s ease",
  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
}));

// Search Icon Wrapper
const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 1.25),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: COMMAND_CENTER_COLORS.text.muted,
  transition: "color 0.2s ease",
}));

// Non-writable box styled to look like an input
const NonWritableBox = styled(Box)(({ theme }) => ({
  color: COMMAND_CENTER_COLORS.text.secondary,
  padding: theme.spacing(0.65, 0.75, 0.65, 0),
  paddingLeft: `calc(1em + ${theme.spacing(3)})`,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.up("md")]: {
    width: "100%",
  },
}));

// Ctrl + K Hint style
const HintText = styled(Typography)(({ theme }) => ({
  marginLeft: "auto",
  backgroundColor: COMMAND_CENTER_COLORS.background.accent,
  borderRadius: "2px",
  padding: theme.spacing(0.25, 0.6),
  color: COMMAND_CENTER_COLORS.text.muted,
  fontSize: "9px",
  fontFamily: mono,
  fontWeight: 700,
  letterSpacing: "0.5px",
  border: `1px solid ${COMMAND_CENTER_COLORS.borders.light}`,
}));

/**
 * Primary Navigation Bar — Intelligence Dashboard Format
 * Professional command center aesthetic with minimal decoration
 */
export default function PrimarySearchAppBar({ className = "" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoggedIn, userPermissions } = useSelector((state) => state.auth);
  const canViewAuditLogs = userPermissions?.some((p) => p.link === "audit-logs" && p.is_allow);

  // Function to open search pop-up
  const handleSearchPopUp = () => {
    dispatch(showSearchPopUP());
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        elevation={0}
        sx={{
          backgroundColor: COMMAND_CENTER_COLORS.background.secondary,
          borderBottom: `1px solid ${COMMAND_CENTER_COLORS.borders.medium}`,
          pr: { xs: "3px", sm: "10px" },
        }}
        className={className}
      >
        <Toolbar sx={{ minHeight: { xs: "52px", sm: "56px" } }}>
          {/* Mobile menu button */}
          <IconButton
            size="small"
            edge="start"
            aria-label="open drawer"
            sx={{
              mr: 0.5,
              display: { xs: "block", md: "none" },
              color: COMMAND_CENTER_COLORS.text.primary,
            }}
            onClick={() => dispatch(toggleSideBar())}
          >
            <MenuIcon sx={{ fontSize: 22 }} />
          </IconButton>

          {/* Logo / App Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              mr: "6px",
              transition: "opacity 0.2s ease",
              "&:hover": {
                opacity: 0.9,
              },
            }}
            onClick={() => navigate("/")}
          >
            {/* Logo */}
            <Box component="img" src={logo} alt="CT Bureau"
              sx={{ width: 48, height: 48, objectFit: "contain", flexShrink: 0 }} />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                noWrap
                sx={{
                  fontFamily: mono,
                  fontSize: "11px",
                  fontWeight: 700,
                  color: COMMAND_CENTER_COLORS.text.primary,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                {AppConstants.appName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: mono,
                  fontSize: "7px",
                  color: COMMAND_CENTER_COLORS.text.muted,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  lineHeight: 1,
                  display: { xs: "none", sm: "block" },
                }}
              >
                THREAT DETECTION SYSTEM
              </Typography>
            </Box>
          </Box>

          {/* Vertical divider after logo */}
          <Box
            sx={{
              width: "1px",
              height: "24px",
              backgroundColor: COMMAND_CENTER_COLORS.borders.light,
              mx: "10px",
              display: { xs: "none", md: "block" },
            }}
          />

          {/* Search field */}
          <Search
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: "space-between",
              flexGrow: 1,
              maxWidth: "450px",
              ml: "6px",
            }}
            onClick={handleSearchPopUp}
          >
            <SearchIconWrapper className="search-icon-el">
              <SearchIcon sx={{ fontSize: 16 }} />
            </SearchIconWrapper>
            <NonWritableBox sx={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  color: COMMAND_CENTER_COLORS.text.muted,
                  fontSize: "11px",
                  fontFamily: mono,
                  letterSpacing: "0.3px",
                }}
              >
                Search incidents, persons, organizations...
              </Typography>
              <HintText variant="body2">CTRL+K</HintText>
            </NonWritableBox>
          </Search>

          <Box sx={{ flexGrow: 1 }} />

          {/* Right section */}
          <Box sx={{ display: "flex", gap: "2px", alignItems: "center" }}>
            {isLoggedIn ? (
              <>
                <IconButton
                  size="small"
                  sx={{ display: { xs: "block", md: "none" } }}
                  onClick={handleSearchPopUp}
                >
                  <SearchIcon sx={{ color: COMMAND_CENTER_COLORS.text.secondary, fontSize: 20 }} />
                </IconButton>
                {/* Audit Logs — Command Director only */}
                {canViewAuditLogs && (
                  <IconButton
                    size="small"
                    onClick={() => navigate("/audit-logs")}
                    sx={{
                      color: COMMAND_CENTER_COLORS.text.muted,
                      borderRadius: "6px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        color: COMMAND_CENTER_COLORS.accent.primary,
                        backgroundColor: COMMAND_CENTER_COLORS.accent.primary + "10",
                      },
                    }}
                    title="Audit Logs"
                  >
                    <ManageSearchIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
                {/* Settings */}
                <IconButton
                  size="small"
                  onClick={() => navigate("/settings")}
                  sx={{
                    color: COMMAND_CENTER_COLORS.text.muted,
                    borderRadius: "6px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: COMMAND_CENTER_COLORS.accent.primary,
                      backgroundColor: COMMAND_CENTER_COLORS.accent.primary + "10",
                    },
                  }}
                  title="Settings"
                >
                  <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
                {/* Help & Support */}
                <IconButton
                  size="small"
                  onClick={() => navigate("/support")}
                  sx={{
                    color: COMMAND_CENTER_COLORS.text.muted,
                    borderRadius: "6px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: COMMAND_CENTER_COLORS.accent.primary,
                      backgroundColor: COMMAND_CENTER_COLORS.accent.primary + "10",
                    },
                  }}
                  title="Help & Support"
                >
                  <HelpOutlineIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <NotificationMenu />
                <Box
                  sx={{
                    width: "1px",
                    height: "24px",
                    backgroundColor: COMMAND_CENTER_COLORS.borders.light,
                    mx: "6px",
                    display: { xs: "none", sm: "block" },
                  }}
                />
                <UserAvatarMenu
                  nameColor={COMMAND_CENTER_COLORS.text.primary}
                  roleColor={COMMAND_CENTER_COLORS.text.muted}
                />
              </>
            ) : (
              <Button
                onClick={() => navigate("/login")}
                sx={{
                  fontFamily: mono,
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  backgroundColor: COMMAND_CENTER_COLORS.accent.primary,
                  color: "#fff",
                  px: 2,
                  py: 0.75,
                  borderRadius: "3px",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: "#0074cc" },
                }}
              >
                Authorize Access
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
